import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment, formatDateTime } from '../../../utils/dateUtils';

import { Box, Button, Typography, Table, TableBody, TableRow, TableCell, TextField, Modal } from '@mui/material';

// Icons
import NoteAddIcon from '@mui/icons-material/NoteAdd';

// Utils
import messages from '../../../static/messages';
import {
  calculateEquipmentTotal,
  calculateScopeOfWorkTotal,
  calculateWarrantyTotal,
  calculateTotalInstallationPrice,
  calculateTotalRebates,
  calculateTotalInstants,
  calculateTotalPrice,
  calculateTotalPay,
} from '../../../utils/proposalCalculations';

// Services
import proposalsServices from '../../../services/proposalsServices';

// Styles
import { button } from '../../../static/theme-styles';
import { modal_style } from '../../../static/styles';

const ProposalPriceTable = ({ proposal, id, refresh, prices }) => {
  const [paymentNote, setPaymentNote] = useState({
    note: '',
    isEdited: false,
  });
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState({
    total: 0,
    rebates: 0,
    instants: 0,
    equipment: 0,
    installation: 0,
    scopeOfWork: 0,
    warranty: 0,
  });

  const confirm = useConfirm();

  // Handler functions
  const handleChange = (event) =>
    setPaymentNote((prevState) => ({
      ...prevState,
      note: event.target.value,
      isEdited: true,
    }));

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (paymentNote.isEdited) {
      confirm({
        title: messages.confirm_message.close_drawer,
        description: messages.confirm_message.close_drawer_description,
      })
        .then(() => {
          setOpen(false);
          setPaymentNote((prevState) => ({
            ...prevState,
            note: proposal.paymentNotes,
          }));
        })
        .catch(() => toast.info(messages.cancel_message));
    } else setOpen(false);
  };

  // Async functions
  const addPaymentNote = async () => {
    delete paymentNote.isEdited;

    try {
      const response = await proposalsServices.update(id, {
        paymentNotes: paymentNote.note,
      });

      if (response.status === 200) {
        handleClose();
        toast.success('Payment notes updated successfully');
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    if (proposal)
      setPaymentNote((prevState) => ({
        ...prevState,
        note: proposal.paymentNotes,
      }));
  }, [proposal]);

  useEffect(() => {
    if (!proposal) {
      setPrice({ total: 0, rebates: 0, instants: 0, installation: 0, equipment: 0, scopeOfWork: 0, warranty: 0 });
      return;
    }
    
    // Use centralized calculation functions
    const equipment = calculateEquipmentTotal(proposal);
    const installation = calculateTotalInstallationPrice(proposal);
    const scopeOfWork = calculateScopeOfWorkTotal(proposal);
    const warranty = calculateWarrantyTotal(proposal);
    const instants = calculateTotalInstants(proposal);
    const rebates = calculateTotalRebates(proposal);
    const total = calculateTotalPrice(proposal);

    setPrice({
      total,
      equipment,
      installation,
      scopeOfWork,
      warranty,
      rebates,
      instants,
    });
  }, [proposal]);

  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const totalToPay = calculateTotalPay(proposal);

  return (
    <Fragment>
      <Table>
        <TableBody>
          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
            <TableCell sx={{ fontWeight: 500, pl: 4 }}>Equipment</TableCell>
            <TableCell>{formatCurrency(price.equipment)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
            <TableCell sx={{ fontWeight: 500, pl: 4 }}>Installation</TableCell>
            <TableCell>{formatCurrency(price.installation)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
            <TableCell sx={{ fontWeight: 500, pl: 4 }}>Scope of Work</TableCell>
            <TableCell>{formatCurrency(price.scopeOfWork)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
            <TableCell sx={{ fontWeight: 500, pl: 4 }}>Warranty</TableCell>
            <TableCell>{formatCurrency(price.warranty)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
            <TableCell sx={{ fontWeight: 600 }}>Total Price (incl. parts and labor)</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(price.total)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>Rebates</TableCell>
            <TableCell sx={{ color: 'success.main' }}>- {formatCurrency(price.rebates)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>Discounts (Instants)</TableCell>
            <TableCell sx={{ color: 'success.main' }}>- {formatCurrency(price.instants)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff3e0' }}>
            <TableCell sx={{ fontWeight: 600 }}>Total After Savings</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(price.total - price.rebates - price.instants)}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#c8e6c9' }}>
            <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Total to Pay</TableCell>
            <TableCell sx={{ fontSize: 18 }}>
              <strong>{formatCurrency(totalToPay)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Button variant="contained" onClick={handleOpen} endIcon={<NoteAddIcon />} sx={{ ...button('secondary'), mt: 2 }}>
        Payment Description
      </Button>

      <Typography variant="h4" sx={{ mt: 2, textAlign: 'center' }}>
        {!!proposal?.signature && (
          <Fragment>
            <Box sx={{ border: '1px solid #ccc', padding: 1 }}>
              <img src={proposal?.signature.sign} alt="signature" style={{ verticalAlign: 'top' }} />
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
              Accepted on {formatDateTime(proposal?.signature?.createdAt)}
            </Typography>
          </Fragment>
        )}
      </Typography>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modal_style}>
          <TextField
            id="paymentNote"
            variant="standard"
            value={paymentNote.note}
            onChange={handleChange}
            label="Payment Description"
            fullWidth
            rows="3"
            multiline
            autoFocus
          />

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={addPaymentNote} size="small" sx={button('primary', 'secondary')}>
              Save
            </Button>

            <Button
              variant="contained"
              onClick={paymentNote.isEdited ? handleClose : () => setOpen(false)}
              size="small"
              sx={{ ...button('secondary'), ml: 1 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Fragment>
  );
};

export default ProposalPriceTable;
